// Series del club (las jornadas semanales) — capa de API. El wrapper HTTP (el 1002 que cierra
// sesión, el motivo de negocio anidado en `data.message`) vive en `apiClient.js`, compartido:
// acá quedan sólo los endpoints y el vocabulario de UI del módulo. Molde: `membershipsApi.js`.
//
// ⚠️ La asimetría del contrato es a propósito y es la de toda la casa: la RESPUESTA trae las
// columnas del modelo en snake_case (`branch_id`, `start_time`, `poster_url`, `active`,
// `notes`) porque el backend spreadea la fila, y sólo lo CALCULADO viaja en camelCase
// (`eventCount`, `nextEvent`, `unclosedCount`, y los campos de las jornadas del tracking). El
// BODY de POST/PATCH, en cambio, va todo en camelCase (`branchId`, `startTime`, `posterUrl`).
// Leer `series.startTime` devuelve `undefined` — es el error que deja la hora vacía al editar.

import { request, post, patch } from './apiClient';

// GET /manage-series?active= — series con su próxima jornada, cuántas llevan y cuántas
// quedaron sin cerrar. Sin `active` vienen todas, las activas primero.
export const listSeries = ({ active } = {}) => {
    const filtra = active !== undefined && active !== null && active !== '';
    return request(`manage-series${filtra ? `?active=${active ? 1 : 0}` : ''}`);
};

// GET /manage-series/:id — la pantalla de la serie: { series, businessDate, nextEvent, events,
// totals, averages }.
export const getSeries = (id) => request(`manage-series/${id}`);

export const createSeries = (payload) => post('manage-series', payload);
export const updateSeries = (id, payload) => patch(`manage-series/${id}`, payload);

// "Nueva jornada" (FR-005) y el alta de la PRIMERA jornada (FR-008) son el mismo endpoint: con
// jornadas previas el body va vacío —la fecha la pone la cadencia de la serie, nunca el
// navegador (TC-002)— y sin ninguna lleva el form de primera jornada
// `{ title?, capacity?, ticketType:{ name, kind, price }, posterUrl?, description? }`.
export const newSession = (id, payload) => post(`manage-series/${id}/events`, payload || {});

// ── vocabulario de UI ──

// 0 = domingo … 6 = sábado, el mismo entero que guarda `event_series.weekday`.
export const WEEKDAYS = [
    { value: 0, label: 'Domingo' },
    { value: 1, label: 'Lunes' },
    { value: 2, label: 'Martes' },
    { value: 3, label: 'Miércoles' },
    { value: 4, label: 'Jueves' },
    { value: 5, label: 'Viernes' },
    { value: 6, label: 'Sábado' },
];

export const weekdayLabel = (weekday) => {
    const dia = WEEKDAYS.find((d) => Number(d.value) === Number(weekday));
    return dia ? dia.label : '—';
};

// La jornada hereda la visibilidad de la serie (S-02e): privada no se lista en la web, pero se
// vende igual en el mostrador y por link directo.
export const VISIBILIDAD_SERIE = {
    public:  'Pública (se lista en la web)',
    private: 'Privada (no se lista en la web)',
};

// Estado de la jornada, que es un `event`. Los mismos cuatro valores que ya rotulan
// `TableEventsComponent` y `EditEventPage`, acá con su badge para la grilla de la serie.
export const ESTADO_JORNADA = {
    draft:     { label: 'Borrador',  badge: 'badge-light' },
    published: { label: 'Publicada', badge: 'badge-success' },
    closed:    { label: 'Cerrada',   badge: 'badge-secondary' },
    cancelled: { label: 'Cancelada', badge: 'badge-danger' },
};

// `start_time` es una columna TIME: llega 'HH:MM:SS' y se muestra 'HH:MM'. Leer los dígitos,
// nunca pasar por `Date` (TC-002).
export const formatStartTime = (value) => {
    const m = /^(\d{1,2}):(\d{2})/.exec(String(value == null ? '' : value).trim());
    return m ? `${String(m[1]).padStart(2, '0')}:${m[2]}` : '—';
};

// Re-export por comodidad de las pantallas de series: la implementación vive con el resto de
// los formateadores de fecha. Dos copias de la misma regla son cómo terminan diciendo cosas
// distintas.
export { formatYMD, formatEventDateTime } from './formats';
